/**
 * QrCode — renders a scannable QR symbol for a URL the caller already holds.
 *
 * SVG (one path, crisp at any size, prints cleanly) with an optional PNG
 * download for posters/door signage. Encoding is local and deterministic
 * (`lib/qrcode.ts`) — no op, no fixture. If the value is too long to encode we
 * render an honest inline message rather than a broken symbol.
 */
import { useMemo } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { encodeQr } from "../lib/qrcode";

const QUIET = 4; // modules of quiet zone, per the spec

function symbolPath(modules: boolean[][]): string {
  let d = "";
  modules.forEach((row, r) => {
    row.forEach((dark, c) => {
      if (dark) d += `M${c + QUIET} ${r + QUIET}h1v1h-1z`;
    });
  });
  return d;
}

export function QrCode({ value, size = 148, testid }: { value: string; size?: number; testid?: string }) {
  const sym = useMemo(() => {
    try { return encodeQr(value); } catch { return null; }
  }, [value]);
  if (!sym) {
    return (
      <div data-testid={testid} style={{ width: size, height: size, display: "grid", placeItems: "center", textAlign: "center", border: `1px dashed ${T.n4}`, borderRadius: 8, fontSize: 11, color: T.soft, padding: 8, lineHeight: 1.4 }}>
        This link is too long to encode as a QR code.
      </div>
    );
  }
  const span = sym.size + QUIET * 2;
  return (
    <svg data-testid={testid} width={size} height={size} viewBox={`0 0 ${span} ${span}`} role="img" aria-label={`QR code for ${value}`}
      style={{ display: "block", borderRadius: 6, background: "#FFFFFF" }} shapeRendering="crispEdges">
      <rect width={span} height={span} fill="#FFFFFF" />
      <path d={symbolPath(sym.modules)} fill="#000000" />
    </svg>
  );
}

/** Download the symbol as a PNG (default 1024px — large enough for print). */
export function downloadQrPng(value: string, filename: string, px = 1024): boolean {
  let sym;
  try { sym = encodeQr(value); } catch { return false; }
  const span = sym.size + QUIET * 2;
  const scale = Math.max(1, Math.floor(px / span));
  const canvas = document.createElement("canvas");
  canvas.width = span * scale;
  canvas.height = span * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000000";
  sym.modules.forEach((row, r) => {
    row.forEach((dark, c) => {
      if (dark) ctx.fillRect((c + QUIET) * scale, (r + QUIET) * scale, scale, scale);
    });
  });
  const a = document.createElement("a");
  a.href = canvas.toDataURL("image/png");
  a.download = filename.endsWith(".png") ? filename : `${filename}.png`;
  a.click();
  return true;
}
